'use client';

import { recordProductEvent } from '@/lib/product-analytics';
import { ResultFeedback } from '@/components/shared/result-feedback';

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { ScheduleRFQModal } from './schedule-rfq-modal';
import { PortInput, type Port } from './port-input';

interface Schedule {
  id: string;
  carrier: string;
  carrier_service_code: string | null;
  carrier_service_name: string | null;
  vessel_name: string | null;
  origin_unlocode: string;
  origin_name: string | null;
  destination_unlocode: string;
  destination_name: string | null;
  departure_date: string | null;
  arrival_date: string | null;
  transit_days: number | null;
  num_transshipments: number | null;
  service_line_codes: string | null;
  distance_km: number | null;
  co2_tonnes: number | null;
  cutoff_date: string | null;
  transshipment_ports: Array<{ port_code: string; port_name: string | null }> | null;
  cutoff_dates: Array<{ code: string; label: string; date: string; estimated?: boolean }> | null;
  origin_terminal: string | null;
  destination_terminal: string | null;
  haulage: string | null;
}

interface Forwarder {
  id: string;
  company_name: string;
  ranking_score: number | null;
  country: string;
  slug: string | null;
  email: string;
  rfq_email: string | null;
  forwarder_type: string | null;
  services: string[] | null;
  created_at: string | null;
}

interface ForwarderMatch {
  forwarders: Forwarder[];
  fallbackForwarders: Forwarder[];
  priority: number | null;
}

interface SearchResult {
  schedules: Schedule[];
  forwarders: {
    origin: ForwarderMatch;
    destination: ForwarderMatch;
  };
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  const [y, m, day] = d.split('-').map(Number);
  const date = new Date(y, m - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

function formatDistance(km: number | null): string | null {
  if (km == null) return null;
  return `${km.toLocaleString('en-US')} km`;
}

function formatCo2(tonnes: number | null): string | null {
  if (tonnes == null) return null;
  return `${tonnes < 10 ? tonnes.toFixed(1) : Math.round(tonnes)}t CO₂`;
}

function transshipmentLabel(n: number | null): string {
  if (n == null || n === 0) return 'Direct';
  return `${n} transshipment${n > 1 ? 's' : ''}`;
}

function getForwardersForDisplay(match: ForwarderMatch): { list: Forwarder[]; isPrimary: boolean } {
  if (match.forwarders.length > 0) {
    return { list: match.forwarders, isPrimary: true };
  }
  return { list: match.fallbackForwarders, isPrimary: false };
}

export function ScheduleWidget() {
  const searchParams = useSearchParams();
  const searchRequest = useRef<AbortController | null>(null);
  useEffect(() => () => searchRequest.current?.abort(), []);
  const [origin, setOrigin] = useState<Port | null>(null);
  const [destination, setDestination] = useState<Port | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [searched, setSearched] = useState(false);
  const [sortBy, setSortBy] = useState<'transit' | 'departure'>('transit');
  const [error, setError] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [rfqSchedule, setRfqSchedule] = useState<Schedule | null>(null);
  const [rfqForwarders, setRfqForwarders] = useState<Forwarder[]>([]);
  const [confirmBanner, setConfirmBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (searchParams.get('confirmed') === 'true') {
      setConfirmBanner({
        type: 'success',
        message: 'Your email has been verified and your quote request has been sent to the freight forwarders. You will receive responses via email.',
      });
      window.history.replaceState({}, '', '/schedules');
    } else if (searchParams.get('error')) {
      const code = searchParams.get('error');
      const messages: Record<string, string> = {
        invalid_link: 'The confirmation link is invalid. Please try submitting a new quote request.',
        expired: 'This quote request has already been confirmed or has expired.',
        invalid_token: 'The confirmation token is invalid. Please try submitting a new quote request.',
      };
      setConfirmBanner({
        type: 'error',
        message: messages[code!] ?? 'Something went wrong. Please try again.',
      });
      window.history.replaceState({}, '', '/schedules');
    }
  }, [searchParams]);

  const resetSearch = () => {
    searchRequest.current?.abort();
    setLoading(false);
    setSearched(false);
    setResult(null);
    setError(null);
    setExpandedCards(new Set());
    setRfqSchedule(null);
    setRfqForwarders([]);
  };

  const handleSearch = async () => {
    if (!origin || !destination || loading) return;
    searchRequest.current?.abort();
    const controller = new AbortController();
    searchRequest.current = controller;
    const startedAt = performance.now();
    recordProductEvent('schedule_search_started', { page_area: 'schedules' });
    setResult(null);
    setLoading(true);
    setSearched(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/schedules?origin=${origin.unlocode}&destination=${destination.unlocode}`,
        { signal: controller.signal }
      );
      if (!res.ok) throw new Error('Failed to fetch schedules');
      const data = await res.json();
      if (controller.signal.aborted) return;
      setResult(data);
      setError(null);
      recordProductEvent('schedule_results_viewed', {
        page_area: 'schedules', results_count: data.schedules?.length || 0,
        outcome: data.schedules?.length ? 'results' : 'empty', duration_ms: Math.round(performance.now() - startedAt),
      });
    } catch {
      if (!controller.signal.aborted) {
        setError('Failed to fetch schedules. Please try again.');
        recordProductEvent('schedule_search_failed', { page_area: 'schedules', duration_ms: Math.round(performance.now() - startedAt) });
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.defaultPrevented && e.target instanceof HTMLInputElement && origin && destination && !loading) {
      e.preventDefault();
      handleSearch();
    }
  };

  const sortedSchedules = result?.schedules.slice().sort((a, b) => {
    if (sortBy === 'transit') {
      const ta = a.transit_days ?? 9999;
      const tb = b.transit_days ?? 9999;
      if (ta !== tb) return ta - tb;
      return (a.departure_date ?? '').localeCompare(b.departure_date ?? '');
    } else {
      const da = a.departure_date ?? '9999';
      const db = b.departure_date ?? '9999';
      if (da !== db) return da.localeCompare(db);
      return (a.transit_days ?? 9999) - (b.transit_days ?? 9999);
    }
  }) ?? [];

  const originDisplay = result ? getForwardersForDisplay(result.forwarders.origin) : null;
  const destDisplay = result ? getForwardersForDisplay(result.forwarders.destination) : null;

  function openRfqModal(schedule: Schedule) {
    const allForwarders: Forwarder[] = [];
    const seen = new Set<string>();
    const addUnique = (list: Forwarder[]) => {
      for (const f of list) {
        if (!seen.has(f.id)) {
          seen.add(f.id);
          allForwarders.push(f);
        }
      }
    };
    if (originDisplay) addUnique(originDisplay.list);
    if (destDisplay) addUnique(destDisplay.list);

    setRfqForwarders(allForwarders);
    setRfqSchedule(schedule);

    recordProductEvent('schedule_quote_started', { page_area: 'schedules', results_count: allForwarders.length });
  }

  const hasAnyForwarders = (originDisplay?.list.length ?? 0) > 0 || (destDisplay?.list.length ?? 0) > 0;

  return (
    <div onKeyDown={handleKeyDown}>
      {confirmBanner && (
        <div
          className={`schedule-confirm-banner schedule-confirm-banner--${confirmBanner.type}`}
          role="alert"
        >
          <span>{confirmBanner.type === 'success' ? '\u2713' : '!'}</span>
          <p>{confirmBanner.message}</p>
          <button
            onClick={() => setConfirmBanner(null)}
            aria-label="Dismiss"
            className="schedule-confirm-banner-close"
          >
            &times;
          </button>
        </div>
      )}
      <div className="schedules-search-form">
        <PortInput
          label="Origin Port"
          placeholder="e.g. Shanghai, Hamburg, Rotterdam"
          value={origin}
          onChange={port => { resetSearch(); setOrigin(port); }}
        />
        <PortInput
          label="Destination Port"
          placeholder="e.g. Los Angeles, Singapore, Dubai"
          value={destination}
          onChange={port => { resetSearch(); setDestination(port); }}
        />
        <button
          className="schedules-search-btn"
          onClick={handleSearch}
          disabled={!origin || !destination || loading}
        >
          {loading ? 'Searching…' : 'See Sailings'}
        </button>
      </div>

      {loading && (
        <div className="schedules-loading">Fetching schedules…</div>
      )}

      {error && !loading && (
        <div className="schedules-empty" style={{ color: '#dc2626', borderColor: '#dc2626' }}>
          <strong>Error</strong> {error}
        </div>
      )}

      {searched && !loading && result && (
        <>
          <div className="schedules-lane">
            <span className="schedules-lane-port">{origin?.name}</span>
            <span className="schedules-lane-arrow">→</span>
            <span className="schedules-lane-port">{destination?.name}</span>
            <span className="schedules-count">
              {result.schedules.length} sailing{result.schedules.length !== 1 ? 's' : ''} found
            </span>
          </div>

          <div className="schedules-results schedules-results--fullwidth">
            <div>
              <div className="schedules-list-header">
                <div className="schedules-section-label">Upcoming Sailings</div>
                {result.schedules.length > 1 && (
                  <div className="schedules-sort">
                    <span className="schedules-sort-label">Sort by</span>
                    <button
                      className={`schedules-sort-btn${sortBy === 'transit' ? ' schedules-sort-btn--active' : ''}`}
                      onClick={() => setSortBy('transit')}
                    >
                      Shortest Transit
                    </button>
                    <button
                      className={`schedules-sort-btn${sortBy === 'departure' ? ' schedules-sort-btn--active' : ''}`}
                      onClick={() => setSortBy('departure')}
                    >
                      Earliest Departure
                    </button>
                  </div>
                )}
              </div>
              {result.schedules.length === 0 ? (
                <div className="schedules-empty">
                  <strong>No sailings found</strong>
                  No carrier schedules are available for this lane yet.
                  Try a different route or check back later.
                </div>
              ) : (
                <div className="schedule-list">
                  {sortedSchedules.map((s) => {
                    const isExpanded = expandedCards.has(s.id);
                    const hasDetails =
                      s.num_transshipments != null ||
                      s.service_line_codes ||
                      s.carrier_service_code ||
                      s.distance_km != null ||
                      s.co2_tonnes != null ||
                      s.cutoff_date ||
                      (s.transshipment_ports && s.transshipment_ports.length > 0) ||
                      (s.cutoff_dates && s.cutoff_dates.length > 0) ||
                      s.origin_terminal ||
                      s.destination_terminal ||
                      s.haulage;

                    return (
                      <div key={s.id} className="schedule-card">
                        <div className="schedule-card-main">
                          <div>
                            <div className="schedule-carrier">{s.carrier}</div>
                            <div className="schedule-dates">
                              <span className="schedule-date-group">
                                <span className="schedule-date-label">Departure</span>
                                <span>{formatDate(s.departure_date)}</span>
                              </span>
                              <span className="schedule-arrow">→</span>
                              <span className="schedule-date-group">
                                <span className="schedule-date-label">Arrival</span>
                                <span>{formatDate(s.arrival_date)}</span>
                              </span>
                            </div>
                            <div className="schedule-meta">
                              {s.carrier_service_name && (
                                <span>{s.carrier_service_name}</span>
                              )}
                              {s.vessel_name && (
                                <span className="schedule-vessel">{s.vessel_name}</span>
                              )}
                            </div>
                          </div>
                          {s.transit_days != null && (
                            <div className="schedule-transit-block">
                              <div className="schedule-transit">{s.transit_days}</div>
                              <div className="schedule-transit-label">days</div>
                            </div>
                          )}
                        </div>

                        {hasDetails && (
                          <button
                            className="schedule-details-toggle"
                            onClick={() => {
                              setExpandedCards((prev) => {
                                const next = new Set(prev);
                                if (next.has(s.id)) next.delete(s.id);
                                else next.add(s.id);
                                return next;
                              });
                            }}
                            aria-expanded={isExpanded}
                          >
                            <span className={`schedule-chevron${isExpanded ? ' schedule-chevron--open' : ''}`}>&#9662;</span>
                            {isExpanded ? 'Hide details' : 'Details'}
                          </button>
                        )}

                        {isExpanded && hasDetails && (
                          <div className="schedule-details">
                            <div className="schedule-details-row">
                              <span className="schedule-detail-item">
                                {transshipmentLabel(s.num_transshipments)}
                              </span>
                              {s.carrier_service_code && (
                                <span className="schedule-detail-item">
                                  Service: {s.carrier_service_code}
                                </span>
                              )}
                              {formatDistance(s.distance_km) && (
                                <span className="schedule-detail-item">
                                  {formatDistance(s.distance_km)}
                                </span>
                              )}
                              {formatCo2(s.co2_tonnes) && (
                                <span className="schedule-detail-item">
                                  {formatCo2(s.co2_tonnes)}
                                </span>
                              )}
                            </div>

                            {s.transshipment_ports && s.transshipment_ports.length > 0 && (
                              <div className="schedule-details-row schedule-ts-ports">
                                <span className="schedule-detail-label">Via:</span>
                                {s.transshipment_ports.map((p, idx) => (
                                  <span key={p.port_code + idx} className="schedule-ts-port">
                                    {p.port_name ?? p.port_code}
                                    <span className="schedule-ts-code">{p.port_code}</span>
                                  </span>
                                ))}
                              </div>
                            )}

                            {s.service_line_codes && (
                              <div className="schedule-details-row">
                                <span className="schedule-detail-item schedule-detail-route">
                                  Route: {s.service_line_codes}
                                </span>
                              </div>
                            )}

                            {(s.haulage || s.origin_terminal || s.destination_terminal) && (
                              <div className="schedule-details-row">
                                {s.haulage && (
                                  <span className="schedule-detail-item">
                                    Haulage: {s.haulage}
                                  </span>
                                )}
                                {s.origin_terminal && (
                                  <span className="schedule-detail-item">
                                    Origin terminal: {s.origin_terminal}
                                  </span>
                                )}
                                {s.destination_terminal && (
                                  <span className="schedule-detail-item">
                                    Destination terminal: {s.destination_terminal}
                                  </span>
                                )}
                              </div>
                            )}

                            {s.cutoff_dates && s.cutoff_dates.length > 0 ? (
                              <div className="schedule-cutoffs">
                                <span className="schedule-detail-label">
                                  Cutoff dates
                                  {s.cutoff_dates.every((c) => c.estimated) ? (
                                    <span
                                      className="schedule-cutoff-estimated"
                                      title="Carrier did not publish a cutoff for this origin — shown as departure − 3 days (industry-typical lead time). Confirm with the booking agent."
                                    >
                                      Estimated
                                    </span>
                                  ) : null}
                                </span>
                                {s.cutoff_dates.map((c) => (
                                  <div key={c.code} className="schedule-cutoff-row">
                                    <span className="schedule-cutoff-label">{c.label}</span>
                                    <span className="schedule-cutoff-date">{formatDate(c.date)}</span>
                                  </div>
                                ))}
                              </div>
                            ) : s.cutoff_date ? (
                              <div className="schedule-details-row">
                                <span className="schedule-detail-item schedule-detail-cutoff">
                                  Cargo cutoff: {formatDate(s.cutoff_date)}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        )}

                        {/* Inline forwarder strip */}
                        <div className="schedule-forwarder-strip">
                          {originDisplay && originDisplay.list.length > 0 && (
                            <div className="schedule-fwd-group">
                              <span className="schedule-fwd-label">
                                {originDisplay.isPrimary ? 'Local Agent at Origin' : 'Featured at Origin'}
                              </span>
                              {originDisplay.list.slice(0, 1).map((f) => (
                                <Link
                                  key={f.id}
                                  href={f.slug ? `/forwarders/${f.slug}` : `/directory?country=${f.country}`}
                                  className={`schedule-fwd-chip${originDisplay.isPrimary ? ' schedule-fwd-chip--primary' : ''}`}
                                >
                                  {f.company_name}
                                  {f.ranking_score != null && (
                                    <span className="schedule-fwd-score">{Math.round(f.ranking_score)}</span>
                                  )}
                                </Link>
                              ))}
                            </div>
                          )}

                          {destDisplay && destDisplay.list.length > 0 && (
                            <div className="schedule-fwd-group">
                              <span className="schedule-fwd-label">
                                {destDisplay.isPrimary ? 'Local Agent at Destination' : 'Featured at Destination'}
                              </span>
                              {destDisplay.list.slice(0, 1).map((f) => (
                                <Link
                                  key={f.id}
                                  href={f.slug ? `/forwarders/${f.slug}` : `/directory?country=${f.country}`}
                                  className={`schedule-fwd-chip${destDisplay.isPrimary ? ' schedule-fwd-chip--primary' : ''}`}
                                >
                                  {f.company_name}
                                  {f.ranking_score != null && (
                                    <span className="schedule-fwd-score">{Math.round(f.ranking_score)}</span>
                                  )}
                                </Link>
                              ))}
                            </div>
                          )}

                          {hasAnyForwarders ? (
                            <button
                              className="schedule-get-quote-btn"
                              onClick={() => openRfqModal(s)}
                            >
                              Get Quote
                            </button>
                          ) : (
                            <Link
                              href={`/directory?country=${destination?.unlocode.substring(0, 2).toUpperCase()}`}
                              className="schedule-get-quote-btn schedule-get-quote-btn--link"
                            >
                              Find Forwarders
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {searched && !loading && (result || error) && <ResultFeedback feature="schedules" outcome={error ? 'error' : result?.schedules.length ? 'results' : 'empty'} />}

      {/* RFQ Modal */}
      <ScheduleRFQModal
        forwarders={rfqForwarders}
        schedule={rfqSchedule ?? {
          carrier: '',
          departure_date: null,
          arrival_date: null,
          origin_unlocode: origin?.unlocode ?? '',
          origin_name: origin?.name ?? null,
          destination_unlocode: destination?.unlocode ?? '',
          destination_name: destination?.name ?? null,
        }}
        isOpen={rfqSchedule !== null}
        onClose={() => setRfqSchedule(null)}
      />
    </div>
  );
}
